import React, { useEffect, useState, useRef } from 'react';
import { Home, Work, Info, Email } from '@mui/icons-material';

import LandingPage from './LandingPage';
import ProjectSection from './ProjectSection';
import About from './About';
import Contact from './Contact';
import Helmet from 'react-helmet';

const Portfolio = () => {
  const [activeSection, setActiveSection] = useState('portfolio-home');
  const hasInteractedRef = useRef(false); // For first interaction-based play
  const landingRef = useRef(null);

  const homeRef = useRef(null);
  const projectsRef = useRef(null);
  const aboutRef = useRef(null);
  const contactRef = useRef(null);

  const sectionRefs = {
    'portfolio-home': homeRef,
    'portfolio-projects': projectsRef,
    'about': aboutRef,
    'contact': contactRef,
  };

  useEffect(() => {
    // 👆 Handle first user interaction (click, scroll, touch, etc.)
    const triggerInitialPlay = () => {
      if (!hasInteractedRef.current) {
        landingRef.current?.playVideo();
        hasInteractedRef.current = true;
      }
    };

    window.addEventListener('click', triggerInitialPlay, { once: true });
    window.addEventListener('touchstart', triggerInitialPlay, { once: true });
    window.addEventListener('keydown', triggerInitialPlay, { once: true });
    window.addEventListener('mousemove', triggerInitialPlay, { once: true });
    window.addEventListener('scroll', triggerInitialPlay, { once: true });

    return () => {
      window.removeEventListener('click', triggerInitialPlay);
      window.removeEventListener('touchstart', triggerInitialPlay);
      window.removeEventListener('keydown', triggerInitialPlay);
      window.removeEventListener('mousemove', triggerInitialPlay);
      window.removeEventListener('scroll', triggerInitialPlay);
    };
  }, []);

  useEffect(() => {
    const sectionIds = Object.keys(sectionRefs);

    const observer = new IntersectionObserver(
      entries => {
        const visible = entries.filter(entry => entry.isIntersecting);
        if (visible.length > 0) {
          const topEntry = visible.sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          const newSection = topEntry.target.id;
          setActiveSection(newSection);

          // 🔁 Every time "Home" becomes visible, play video
          if (newSection === 'portfolio-home') {
            landingRef.current?.playVideo();
          }
        }
      },
      { rootMargin: '0px 0px -40% 0px', threshold: 0.4 }
    );

    sectionIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const isActive = id => (activeSection === id ? 'active' : '');

  const handleNavLinkClick = (sectionId) => {
    const target = sectionRefs[sectionId]?.current;
    if (target) {
      const offset = sectionId === 'portfolio-projects' ? 80 : 50;
      window.scrollTo({
        top: target.offsetTop - (sectionId === 'portfolio-home' ? 0 : offset),
        behavior: 'smooth',
      });

      setActiveSection(sectionId);

      // 🔁 Play video again when navigating to Home via navbar
      if (sectionId === 'portfolio-home') {
        landingRef.current?.playVideo();
      }
    }
  };

  return (
    <div className="portfolio-wrapper">
      <Helmet>
        <title>NavarroJalen | Portfolio</title>
      </Helmet>

      <div className="portfolioMainDiv">
        <nav className="portfolio-navbar">
          <a onClick={() => handleNavLinkClick('portfolio-home')} className={`nav-link ${isActive('portfolio-home')}`}>
            <Home className="nav-icon" /><span className="nav-text">Home</span>
          </a>
          <a onClick={() => handleNavLinkClick('portfolio-projects')} className={`nav-link ${isActive('portfolio-projects')}`}>
            <Work className="nav-icon" /><span className="nav-text">Projects</span>
          </a>
          <a onClick={() => handleNavLinkClick('about')} className={`nav-link ${isActive('about')}`}>
            <Info className="nav-icon" /><span className="nav-text">About</span>
          </a>
          <a onClick={() => handleNavLinkClick('contact')} className={`nav-link ${isActive('contact')}`}>
            <Email className="nav-icon" /><span className="nav-text">Contact</span>
          </a>
        </nav>

        <div id="portfolio-home" className="landingPage-wrapper" ref={homeRef}>
          <LandingPage ref={landingRef} />
        </div>
        <div id="portfolio-projects" className="projectSection-wrapper" ref={projectsRef}>
          <ProjectSection />
        </div>
        <div id="about" className="about-wrapper" ref={aboutRef}>
          <About />
        </div>
        <div id="contact" className="contact-wrapper" ref={contactRef}>
          <Contact />
        </div>
      </div>
    </div>
  );
};

export default Portfolio;
